import React, { useEffect, useState, useRef } from 'react';
import { dataStructure, colors, geoStructure } from './components/DataStructure';
import { FilterButton } from './components/Icons';
import { dateRange, delayTodayDateByMonths } from './components/functions/dateFunctions';
import Map from './components/Map';
import Modal from './components/Modal';
import ProjectSidebar from './components/ProjectSidebar';
import FilterSidebar from './components/FilterSidebar';
import projDataAll from './data/projects.geojson';
import projDataRepd from './data/projects_repd.geojson';
import projDataHeatNetwork from './data/projects_heat.geojson';
import './stylesheets/App.css';

const searchParams = new URLSearchParams(window.location.search);
const projectType = searchParams.get('type')
const projData = projectType === "repd" ? projDataRepd :
  projectType === "heat_network" ? projDataHeatNetwork : projDataAll
const today = delayTodayDateByMonths(1).toISOString().slice(0, 10)
const bounds = [[-10.5571271, 48.0186281], [2.0000000, 60.8406010]];

const App = (props) => {

  const [mapObject, setMapObject] = useState(undefined);
  const [allProjects, setAllProjects] = useState(geoStructure);
  const [techTypes, setTechTypes] = useState([]);
  const [statusTypes, setStatusTypes] = useState([]);
  const [regionTypes, setRegionTypes] = useState([]);
  const [sourceTypes, setSourceTypes] = useState([]);
  const [heatNetworkType, setHeatNetworkType] = useState([]);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [projectActive, setProjectActive] = useState(false);
  const [projectDetails, setProjectDetails] = useState(dataStructure.project);
  const [projects, setProjects] = useState({ features: [] });
  const [filterSidebarActive, setFilterSidebarActive] = useState(true);
  const [filterOpen, setFilterOpen] = useState('');
  const [dateBounds, setDateBounds] = useState([0, 0]);
  const [capacityBounds, setCapacityBounds] = useState([0, 100]);
  const [filterActive, setFilterActive] = useState({ energy: [''], status: [''], region: [''], source: [''], heatNetwork: [''], building: [''] })
  const [dateIndexValue, setDateIndexValue] = useState([0, dateRange('1990-04-01', today).length - 1]);
  const [capacityIndexValue, setCapacityIndexValue] = useState([0, 100]);
  const [dateFilterActive, setDateFilterActive] = useState(false);
  const [capacityFilterActive, setCapacityFilterActive] = useState(false);
  const [heatmapLayerActive, setHeatmapLayerActive] = useState(false);
  const [boundaryLayerActive, setBoundaryLayerActive] = useState(false);
  const [buildingTypeActive, setBuildingTypeActive] = useState(false);
  const [modalActive, setModalActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [connectionBounds, setConnectionBounds] = useState([1, 999]);
  const [connectionIndexValue, setConnectionIndexValue] = useState([1, 999]);
  const [connectionFilterActive, setConnectionFilterActive] = useState(false);
  const dateRangeRef = useRef(null);
  const capacityRangeRef = useRef(null);
  const connectionRangeRef = useRef(null);

  const getWindowDimensions = () => {
    const { innerWidth: width } = window;
    return width;
  }

  const useWindowDimensions = () => {
    const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
    useEffect(() => {
      function handleResize() {
        setWindowDimensions(getWindowDimensions());
      }
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowDimensions;
  }

  const width = useWindowDimensions();

  useEffect(() => {
    fetch(projData)
      .then(res => res.json())
      .then(data => {
        setAllProjects(data);
        const tech = [...new Set(data.features.map(p => p.properties.tech))].sort();
        const stat = [...new Set(data.features.map(p => p.properties.stat))].sort();
        const reg = [...new Set(data.features.map(p => p.properties.reg))].sort();
        const src = [...new Set(data.features.map(p => p.properties.src))].sort();
        const hnt = [...new Set(data.features.map(p => p.properties.hnt))].sort();
        const blg = [...new Set(data.features.map(p => p.properties.blg))].sort();
        const hntFilter = hnt.filter(element => { return element !== null; });
        const blgFilter = blg.filter(element => { return element !== null; });
        setDateBounds(dateRange('1990-04-01', today));
        setCapacityBounds([0, 100]);
        setConnectionBounds([0, 999]);
        setTechTypes(tech);
        setStatusTypes(stat);
        setRegionTypes(reg);
        setSourceTypes(src);
        setHeatNetworkType(hntFilter);
        setBuildingTypes(blgFilter);
        if (width < 769) { setFilterSidebarActive(false) }
        setFilterActive(
          {
            energy: tech,
            status: stat,
            region: reg,
            source: src,
            heatNetwork: hntFilter,
            building: blgFilter
          });
      })
      .catch(err => console.log(err))
  }, [width]);

  useEffect(() => {
    const mapProjects = {
      ...allProjects,
      features: (
        allProjects.features.filter(
          proj =>
            projectType === 'heat_network' ? 
            filterActive.energy.includes(proj.properties.tech) &&
            filterActive.status.includes(proj.properties.stat) &&
            filterActive.region.includes(proj.properties.reg) &&
            filterActive.source.includes(proj.properties.src) &&
            filterActive.heatNetwork.includes(proj.properties.hnt) &&
            filterActive.building.includes(proj.properties.blg) &&
            (new Date(proj.properties.app) > new Date(dateBounds[dateIndexValue[0]]) || (!dateFilterActive && proj.properties.app === null)) &&
            (new Date(proj.properties.app) <= new Date(dateBounds[dateIndexValue[1]])) &&
            (proj.properties.capa > capacityIndexValue[0] || (!capacityFilterActive && proj.properties.capa === null)) &&
            ((capacityIndexValue[1] === capacityBounds[1]) ? proj.properties.capa < 999999999999999 : proj.properties.capa < capacityIndexValue[1])
            &&
            ((proj.properties.noc === undefined) ? (proj.properties.capa > capacityIndexValue[0] || (!capacityFilterActive && proj.properties.capa === null)) &&
              ((capacityIndexValue[1] === capacityBounds[1]) ? proj.properties.capa < 999999999999999 : proj.properties.capa < capacityIndexValue[1]) :
              (proj.properties.noc >= connectionIndexValue[0] || (!connectionFilterActive && proj.properties.noc === null))
              &&
              ((connectionIndexValue[1] === connectionBounds[1]) ? proj.properties.noc < 999999999999999 : proj.properties.noc < connectionIndexValue[1]))
          :
              filterActive.energy.includes(proj.properties.tech) &&
              filterActive.status.includes(proj.properties.stat) &&
              filterActive.region.includes(proj.properties.reg) &&
              filterActive.source.includes(proj.properties.src) &&
              // filterActive.heatNetwork.includes(proj.properties.hnt) &&
              // filterActive.building.includes(proj.properties.blg) &&
              (new Date(proj.properties.app) > new Date(dateBounds[dateIndexValue[0]]) || (!dateFilterActive && proj.properties.app === null)) &&
              (new Date(proj.properties.app) <= new Date(dateBounds[dateIndexValue[1]])) &&
              (proj.properties.capa > capacityIndexValue[0] || (!capacityFilterActive && proj.properties.capa === null)) &&
              ((capacityIndexValue[1] === capacityBounds[1]) ? proj.properties.capa < 999999999999999 : proj.properties.capa < capacityIndexValue[1])
              &&
              ((proj.properties.noc === undefined) ? (proj.properties.capa > capacityIndexValue[0] || (!capacityFilterActive && proj.properties.capa === null)) &&
                ((capacityIndexValue[1] === capacityBounds[1]) ? proj.properties.capa < 999999999999999 : proj.properties.capa < capacityIndexValue[1]) :
                (proj.properties.noc >= connectionIndexValue[0] || (!connectionFilterActive && proj.properties.noc === null))
                &&
                ((connectionIndexValue[1] === connectionBounds[1]) ? proj.properties.noc < 999999999999999 : proj.properties.noc < connectionIndexValue[1]))
        )
      )
    }
    setProjects(mapProjects)
  }, [allProjects, filterActive, dateBounds, dateIndexValue, dateFilterActive, capacityIndexValue, capacityBounds, capacityFilterActive, connectionIndexValue, connectionBounds, connectionFilterActive])

  const handleMapObject = m => {
    setMapObject(m);
    pulsingMarker(m);
    setIsLoading(false);
  }

  const handleProjectActive = e => {
    if (e === false) {
      setProjectActive(false);
      setProjectDetails(dataStructure.project);
      return;
    }

    setProjectActive(true);
    setProjectDetails(e);
  }

  const handleReset = () => {
    centerMap(mapObject);
    setProjectActive(false);
    setProjectDetails(dataStructure.project);
    setFilterOpen('');
    resetDateRange();
    resetCapacityRange()
    if (projectType === 'heat_network') {
      resetConnectionRange()
    }
    setDateFilterActive(false);
    setCapacityFilterActive(false);
    setConnectionFilterActive(false);
    setDateIndexValue([0, dateRange('1990-04-01', today).length - 1]);
    setCapacityIndexValue([0, 100]);
    setConnectionIndexValue([0, 999]);
    setHeatmapLayerActive(false);
    setBuildingTypeActive(false);
    setBoundaryLayerActive(false);
    setFilterActive(
      {
        energy: techTypes,
        status: statusTypes,
        region: regionTypes,
        source: sourceTypes,
        heatNetwork: heatNetworkType,
        building: buildingTypes
      }
    );

  }

  const handleFilterSidebar = () => {
    setFilterSidebarActive(!filterSidebarActive)
  }

  const handleFilter = e => {
    (filterOpen === e.currentTarget.id) ? setFilterOpen('') : setFilterOpen(e.currentTarget.id);
  }

  const handleFilterEntry = (e, val, allOptions, type = 'single') => {

    handleProjectActive(false);
    if (type === 'multi' && allOptions.length === filterActive[val].length) {
      setFilterActive(
        prevState => {
          return { ...prevState, [val]: [] };
        }
      );
    }

    else if (type === 'multi' && allOptions.length !== filterActive[val].length) {
      setFilterActive(
        prevState => {
          return { ...prevState, [val]: allOptions };
        }
      );
    }

    else if (filterActive[val].includes(e.currentTarget.id)) {
      const arr = filterActive[val].filter(item => item !== e.currentTarget.id);
      setFilterActive(
        prevState => {
          return { ...prevState, [val]: arr };
        }
      );
      return;
    }

    else {
      const arr = filterActive[val];
      arr.push(e.currentTarget.id);
      setFilterActive(
        prevState => {
          return { ...prevState, [val]: arr };
        }
      );
    }
  }

  const centerMap = m => m.fitBounds(bounds, {
    padding: ((width > 769) ? { top: 50, bottom: 50, left: 350, right: 50 } : { top: 20, bottom: 20, left: 10, right: 10 })
  });

  const calculateColorPalette = (techTypes, colors) => {
    const pal = Object.keys(techTypes).reduce((arr, t) => {
      const keyVal = techTypes[t];
      arr[keyVal] = colors[keyVal];
      return arr;
    }, {});
    return pal;
  };

  const colorPalette = calculateColorPalette(techTypes, colors)

  const projectCount = () => {
    if (allProjects.features.length === projects.features.length) return allProjects.features.length;
    return projects.features.length;
  }

  const pulsingMarker = (map) => {

    let size = 150;

    // implementation of CustomLayerInterface to draw a pulsing dot icon on the map
    // see https://docs.mapbox.com/mapbox-gl-js/api/#customlayerinterface for more info
    let pulsingDot = {
      width: size,
      height: size,
      data: new Uint8Array(size * size * 4),

      // get rendering context for the map canvas when layer is added to the map
      onAdd: function () {
        let canvas = document.createElement('canvas');
        canvas.width = this.width;
        canvas.height = this.height;
        this.context = canvas.getContext('2d');
      },

      // called once before every frame where the icon will be used
      render: function () {
        const duration = 1000;
        let t = (performance.now() % duration) / duration;

        const radius = (size / 2) * 0.3;
        const outerRadius = (size / 2) * 0.7 * t + radius;
        let context = this.context;

        // draw outer circle
        context.clearRect(0, 0, this.width, this.height);
        context.beginPath();
        context.arc(
          this.width / 2,
          this.height / 2,
          outerRadius,
          0,
          Math.PI * 2
        );
        context.fillStyle = 'hsla(206, 36%, 50%, ' + (0.7 - t) + ')';
        context.fill();


        // update this image's data with data from the canvas
        this.data = context.getImageData(
          0,
          0,
          this.width,
          this.height
        ).data;

        // continuously repaint the map, resulting in the smooth animation of the dot
        map.triggerRepaint();
        return true;
      },
    };

    map.addImage('pulsing-dot', pulsingDot, { pixelRatio: 2.4 });
  };

  const resetDateRange = () => {
    dateRangeRef.current.resetIndexValueGhost();
  };

  const resetCapacityRange = () => {
    capacityRangeRef.current.resetIndexValueGhost();
  };

  const resetConnectionRange = () => {
    connectionRangeRef.current.resetIndexValueGhost();
  };


  return (
    <div className='app dark-mode'>
      {isLoading &&
        <div className='loader'>
          <div className='loader-container'>
            <div className='loader-circle-container'>
              <div className="loader-circle" />
            </div>
            <div className='loader-logo-container'>
              <img
                className='loader-logo'
                src={process.env.PUBLIC_URL + '/images/DESNZ_invert.png'}
                alt='Department for Business Energy and Insdustrial Strategy' />
            </div>



          </div>
        </div>
      }
      {allProjects &&
        <Map
          projects={projects}
          handleProjectActive={handleProjectActive}
          handleMapObject={handleMapObject}
          techTypes={techTypes}
          projectActive={projectActive}
          projectDetails={projectDetails}
          heatmapLayerActive={heatmapLayerActive}
          buildingTypeActive={buildingTypeActive}
          boundaryLayerActive={boundaryLayerActive}
          width={width}
        />
      }

      {modalActive &&
        <Modal
          setModalActive={setModalActive}
          projectType={projectType} />
      }

      <FilterSidebar
        filterSidebarActive={filterSidebarActive}
        setFilterSidebarActive={setFilterSidebarActive}
        handleFilterSidebar={handleFilterSidebar}
        handleReset={handleReset}
        colorPalette={colorPalette}
        techTypes={techTypes}
        statusTypes={statusTypes}
        regionTypes={regionTypes}
        sourceTypes={sourceTypes}
        heatNetworkType={heatNetworkType}
        buildingTypes={buildingTypes}
        handleFilter={handleFilter}
        filterOpen={filterOpen}
        handleFilterEntry={handleFilterEntry}
        filterActive={filterActive}
        projectCount={projectCount()}
        dateBounds={dateBounds}
        capacityBounds={capacityBounds}
        connectionBounds={connectionBounds}
        setDateIndexValue={setDateIndexValue}
        dateFilterActive={dateFilterActive}
        setDateFilterActive={setDateFilterActive}
        capacityFilterActive={capacityFilterActive}
        connectionFilterActive={connectionFilterActive}
        setCapacityFilterActive={setCapacityFilterActive}
        setConnectionFilterActive={setConnectionFilterActive}
        setCapacityIndexValue={setCapacityIndexValue}
        setConnectionIndexValue={setConnectionIndexValue}
        dateRangeRef={dateRangeRef}
        capacityRangeRef={capacityRangeRef}
        connectionRangeRef={connectionRangeRef}
        dateIndexValue={dateIndexValue}
        setHeatmapLayerActive={setHeatmapLayerActive}
        setBuildingTypeActive={setBuildingTypeActive}
        heatmapLayerActive={heatmapLayerActive}
        buildingTypeActive={buildingTypeActive}
        setBoundaryLayerActive={setBoundaryLayerActive}
        boundaryLayerActive={boundaryLayerActive}
        today={today}
        setModalActive={setModalActive}
        projectType={projectType} />

      <ProjectSidebar
        projectActive={projectActive}
        projectDetails={projectDetails.properties}
        colorPalette={colorPalette}
        handleProjectActive={handleProjectActive} />

      <div className={(width > 769) ? 'filter-button-hidden' : ''} onClick={() => setFilterSidebarActive(!filterSidebarActive)}>
        <FilterButton
          className={'filter-button'} />
      </div>
    </div>
  );
};

export default App;
